import { useState, useEffect } from 'react'
import { api } from '../lib/api'
import { FileText, Download, X, Loader2, Image as ImageIcon } from 'lucide-react'

const OFFICE_EXT = ['.docx', '.doc', '.xlsx', '.xls', '.pptx', '.ppt']

function isOfficeFile(mimeType: string, fileName: string): boolean {
  const m = (mimeType || '').toLowerCase()
  if (m.includes('word') || m.includes('spreadsheet') || m.includes('presentation') || m.includes('msword') || m.includes('ms-excel') || m.includes('ms-powerpoint')) return true
  const ext = (fileName || '').toLowerCase()
  return OFFICE_EXT.some((e) => ext.endsWith(e))
}

export default function OneDriveFilePreview({
  isOpen,
  onClose,
  filePath,
  fileName,
  mimeType,
}: {
  isOpen: boolean
  onClose: () => void
  filePath: string
  fileName: string
  mimeType: string
}) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [onlyofficeConfig, setOnlyofficeConfig] = useState<{
    docServerUrl: string
    documentType: string
    document: { url: string; key: string; fileType: string; title: string }
    editorConfig: { callbackUrl: string }
    token?: string | null
  } | null>(null)
  const editorDivId = 'od-onlyoffice-editor-' + encodeURIComponent(filePath).slice(0, 40)
  const base = api.defaults.baseURL || '/api'
  const pathQ = encodeURIComponent(filePath)
  const viewUrl = `${base}/onedrive/files/view?path=${pathQ}`
  const downloadUrl = `${base}/onedrive/files/download?path=${pathQ}`
  const isPDF = mimeType === 'application/pdf'
  const isImage = mimeType?.startsWith('image/') ?? false
  const isOffice = isOfficeFile(mimeType, fileName)

  useEffect(() => {
    if (isOpen) {
      setLoading(true)
      setError(false)
      setOnlyofficeConfig(null)
    }
  }, [isOpen, filePath])

  useEffect(() => {
    if (!isOpen || !isOffice || !filePath) return
    let cancelled = false
    api
      .get('/onedrive/files/onlyoffice-config', { params: { path: filePath } })
      .then((res) => {
        if (cancelled) return
        const data = res.data
        if (data?.docServerUrl && data?.document && data?.editorConfig) {
          setOnlyofficeConfig({
            docServerUrl: data.docServerUrl,
            documentType: data.documentType || 'word',
            document: data.document,
            editorConfig: data.editorConfig,
            token: data.token ?? null,
          })
        } else {
          setOnlyofficeConfig(null)
          setLoading(false)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setOnlyofficeConfig(null)
          setLoading(false)
        }
      })
    return () => { cancelled = true }
  }, [isOpen, isOffice, filePath])

  useEffect(() => {
    if (!onlyofficeConfig || !isOpen) return
    const config = onlyofficeConfig
    const docServerUrl = config.docServerUrl.replace(/\/$/, '')
    const scriptUrl = `${docServerUrl}/web-apps/apps/api/documents/api.js`
    const initEditor = () => {
      const DocsAPI = (window as unknown as { DocsAPI?: { DocEditor: new (id: string, cfg: object) => void } }).DocsAPI
      if (!DocsAPI?.DocEditor) {
        setLoading(false)
        return
      }
      try {
        const editorConfig: Record<string, unknown> = {
          documentType: config.documentType,
          document: config.document,
          editorConfig: config.editorConfig,
        }
        if (config.token) editorConfig.token = config.token
        new DocsAPI.DocEditor(editorDivId, editorConfig)
        setLoading(false)
      } catch {
        setLoading(false)
      }
    }
    const existing = document.querySelector(`script[src="${scriptUrl}"]`)
    if (existing) {
      initEditor()
      return
    }
    const script = document.createElement('script')
    script.src = scriptUrl
    script.onload = initEditor
    script.onerror = () => setLoading(false)
    document.head.appendChild(script)
  }, [onlyofficeConfig, isOpen, editorDivId])

  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-2" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-[96vw] max-w-[1800px] h-[96vh] flex flex-col dark:bg-gray-900" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <span className="text-lg font-medium truncate dark:text-gray-100" title={fileName}>{fileName}</span>
          <div className="flex items-center gap-1">
            <a href={downloadUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-800">
              <Download className="h-4 w-4" /> Herunterladen
            </a>
            <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"><X className="h-5 w-5" /></button>
          </div>
        </div>
        <div className="flex-1 overflow-hidden relative bg-gray-100 dark:bg-gray-950">
          {error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-8">
              <p className="text-gray-600 dark:text-gray-400 mb-2">Vorschau konnte nicht geladen werden</p>
            </div>
          )}
          {!error && isPDF && (
            <iframe src={viewUrl} className="w-full h-full border-0" title={fileName} onLoad={() => setLoading(false)} onError={() => { setLoading(false); setError(true) }} />
          )}
          {!error && isImage && (
            <img src={viewUrl} alt={fileName} className="max-w-full max-h-full object-contain mx-auto" onLoad={() => setLoading(false)} onError={() => { setLoading(false); setError(true) }} />
          )}
          {!error && isOffice && onlyofficeConfig && (
            <div id={editorDivId} className="w-full h-full min-h-[60vh]" />
          )}
          {!error && isOffice && loading && !onlyofficeConfig && (
            <div className="absolute inset-0 flex items-center justify-center"><Loader2 className="h-10 w-10 animate-spin text-primary-600" /></div>
          )}
          {!error && !isPDF && !isImage && (!isOffice || (!onlyofficeConfig && !loading)) && (
            <div className="flex flex-col items-center justify-center p-8">
              <FileText className="h-16 w-16 text-gray-400 mb-4" />
              <p className="text-gray-600 dark:text-gray-400 mb-4">Vorschau für diesen Dateityp nicht verfügbar</p>
              <a href={downloadUrl} className="btn btn-primary inline-flex items-center gap-2"><Download className="h-4 w-4" /> Herunterladen</a>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
